'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { X, GitCompare, ArrowRight } from 'lucide-react';
import { useCompareStore } from '@/stores/compare';

const PLACEHOLDER = '/placeholder-product.svg';

/**
 * Floating bar fixed to the bottom-right (above the mobile sticky buy bar on
 * product pages, plain bottom otherwise). Hidden when the user is already on
 * /jamfor, or when no items are selected.
 */
export function CompareFloatingBar() {
  const items = useCompareStore((s) => s.items);
  const remove = useCompareStore((s) => s.remove);
  const clear = useCompareStore((s) => s.clear);
  const pathname = usePathname();

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted) return null;
  if (items.length === 0) return null;
  if (pathname === '/jamfor') return null;

  // Float higher on product pages so it doesn't overlap the sticky buy bar.
  const isPdp = pathname.startsWith('/produkt/');
  const bottomOffset = isPdp
    ? 'bottom-20 sm:bottom-4'
    : 'bottom-4';

  return (
    <div className={`fixed ${bottomOffset} right-3 sm:right-6 z-40 max-w-[calc(100vw-1.5rem)]`}>
      <div className="bg-white rounded-2xl shadow-2xl border-2 border-[#0055A4] p-3 sm:p-4 flex items-center gap-3">
        {/* Thumbnails (max 3 visible) */}
        <div className="flex -space-x-2">
          {items.slice(0, 3).map((item) => (
            <div
              key={item.id}
              className="relative h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-white border-2 border-gray-100 overflow-hidden"
            >
              <Image
                src={item.image || PLACEHOLDER}
                alt={item.name}
                fill
                sizes="48px"
                className="object-contain p-0.5"
                referrerPolicy="no-referrer"
              />
            </div>
          ))}
          {items.length > 3 && (
            <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-gray-100 border-2 border-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600">
              +{items.length - 3}
            </div>
          )}
        </div>

        {/* Label + actions */}
        <div className="flex flex-col gap-1">
          <p className="text-[11px] text-gray-500 leading-tight">
            <span className="font-semibold text-gray-900">{items.length}</span>
            <span className="hidden sm:inline"> i jämförelse</span>
            <span className="sm:hidden">/4</span>
          </p>
          <div className="flex items-center gap-2">
            <Link
              href="/jamfor"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#0055A4] hover:bg-[#0f2e5a] text-white text-xs sm:text-sm font-semibold"
            >
              <GitCompare className="h-3.5 w-3.5" />
              Jämför
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <button
              type="button"
              onClick={clear}
              className="text-xs text-gray-500 hover:text-gray-900 underline"
              aria-label="Töm jämförelse"
            >
              Töm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
